import { IChatData } from './api';
import { IIMAccountInfo } from './auth';
import { INpcInfoWithExtra } from './chat';

export interface IAuthData {
  authToken: string;
  isNew?: boolean;
  isAnonymous: boolean;
  referralCode?: string;
  refreshToken?: string;
  userId: number;
  email?: string;
  deviceId?: number;
  uniqueId?: number;
  imAccountInfo?: IIMAccountInfo;
  uuid?: string;
  imToken?: string;
}

export interface IGetUserProfileReq {
  user_id: number;
}

interface ILocation {
  country?: string;
  province?: string;
  city?: string;
}

export interface IUserChatPrompt {
  title?: string; //title
  gender?: EUserGender;
  whoiam?: string; //我是谁
  enable?: boolean; //是否开启
  tone?: { [key: string]: number }; //音色
  speed?: number; // 语速 [0.5-1.5] 默认1.0
  pitch?: number; // 音调 [-12, 12] 默认0
}

interface IEditLimit {
  field: string; // 字段名
  period: number; // 限制周期, xx 天
  limit: number; // 限制，yy次,目前固定为1
  enable_at: number; // 下次可以编辑的时间
}

interface IUserProfile {
  user_id: number;
  name?: string;
  avatar_url?: string;
  profile_desc?: string; // 个人简介
  school?: string;
  enrollment_year?: number; // 入学年份
  gender?: EUserGender; // 性别 // 0: 未知 1: 男 2: 女
  birthday?: string; // 生日,连字符,MM-DD
  zodiac_sign?: number; // 星座,1-12，read only, 通过生日计算,不可以修改
  location?: ILocation; // 地址
  is_default_nickname: boolean; // 是否是默认昵称,readonly
  reviewing?: boolean; // 是否正在审核
  chat_prompt?: IUserChatPrompt; // 对话人设
  edit_limits: { [key: string]: IEditLimit }; // 编辑限制, key: 字段名, value: 编辑限制.目前可能的key 有: name, desc, school
  display_gender?: boolean; // 个人页是否显示性别
  display_school?: boolean; // 个人页是否显示学校，
  display_zodiac_sign?: boolean; // 个人页是否显示星座
}

export interface IUpdateUserProfileV2Req {
  profile?: IUserProfile;
}

export interface IUpdateUserProfileResp {
  profile?: IUserProfile;
}

interface IBanner {
  banner_id?: number; //运营位id
  duration?: number; //展示时长
  title?: string; //标题
  content?: string; //内容
  image_url?: string; //图片地址
  jump_link?: string; //跳转链接
}

export enum EUserGender {
  None = 0,
  Male = 1,
  Female = 2,
  Unknown = 3,
}

export enum ESubscribeType {
  Default = 0, // 未订阅
  Subscribing = 1, // 我订阅他，他不订阅我
  Subscribed = 2, // 他订阅我，我不订阅他
  MutualSubscribe = 3, // 互相订阅
}

interface IUserStatisticInfo {
  following_count?: number; // 当前用户正在关注的数量
  linking_count?: number; // 当前用户聊过的NPC数
  linking_group_count?: number;
  subscribers_count?: number;
  subscribing_count?: number;
}
enum EPrivilegeType {
  None = 0, // 没有认证
  Official = 1, // 官方认证
  Individual = 2, // 个人认证
  Business = 3, // 企业认证
}

interface IHyperlink {
  name: string;
  href: string;
}

interface IPrivilegeInfo {
  privilege_type: EPrivilegeType; // 认证类型
  privilege_name: string; // 认证名称
  description?: string; // 认证描述
  hyper_links?: IHyperlink[]; // 超链接
}

enum CountType {
  CountPass = 1,
  ThisWeekLimit = 2,
  TotalLimit = 3,
}

enum UgcItemType {
  Npc = 1,
  GroupTemplate = 2,
}

interface INpcCount {
  this_week_count?: number;
  this_week_limit?: number;
  total_count?: number;
  total_limit?: number;
  next_refresh_ts?: number;
  count_type?: CountType;
}

export interface IGetUserProfileResp {
  user_id: number;
  unique_id: number;
  nickname: string;
  avatar_url: string;
  has_notice?: boolean; // 是否有未读通知
  banners?: IBanner[]; // banner
  gender?: EUserGender; // 性别
  user_desc?: string; // 人设
  set_for_chat?: boolean; // 是否设置为聊天
  subscribe_status?: ESubscribeType;

  statistic_info?: IUserStatisticInfo; // 统计信息
  privilege_info?: IPrivilegeInfo; // 用户认证信息
  npc_count?: INpcCount; // npc 数量信息
}

export interface IFirebaseUser {
  loginAccount: string;
  loginType: string;
}

export interface IGetMyCreaetNpcReq {
  user_id?: number;
  page?: number;
  size?: number;
  viewer_user_id?: number;
}

export interface IGetMyCreaetNpcResp {
  npc_list: INpcInfoWithExtra[];
  has_more: boolean;
  page: number;
  npc_count: INpcCount;
}

export interface IGetFollowingItemListReq {
  user_id?: number;
  page?: number;
  size?: number;
}

export interface IGetFollowingItemListResp {
  has_more?: boolean;
  item_list?: IChatData[];
}

export interface INpcListItem {
  npc_id: number;
  user_id: number;
  followers_count?: number;
  linkers_count?: number;
  im_account?: string;
  follow_status?: number;
  link_status?: number;
  npc_name: string;
  npc_desc?: string;
  thumbnail_avatar_url: string;
  privacy_setting?: number;
}

export interface IGetUserLinkingListReq extends IGetFollowingItemListReq {}
export interface IGetUserLinkingListResp {
  has_more?: boolean;
  npc_list?: INpcListItem[];
}

export interface IUserBaseInfo {
  user_id: number;
  unique_id: number;
  nickname: string;
  avatar_url?: string;
}

export interface ISubscribingUserBaseInfo extends IUserBaseInfo {
  // 0-unsubscribed, 1-subscribed
  subscribe_status: ESubscribeType;
  subscribers_count: number;
}

export interface IGetUserSubscribingListReq extends IGetFollowingItemListReq {}

export interface IGetSubscribingItemListResp {
  has_more?: boolean;
  user_list?: ISubscribingUserBaseInfo[];
}

export interface IUpdateUserChatPromptReq {
  npc_id?: number;
  user_chat_prompt?: IUserChatPrompt;
}

export enum EUserChatPromptScenario {
  None = 0, // 人设未设定
  NPC = 1, // 局部人设
  Global = 2, // 全局人设
}

export interface IGetUserChatPromptReq {
  npc_id?: number;
}

export interface IGetUserChatPromptResp {
  user_chat_prompt?: IUserChatPrompt;
  scenario?: EUserChatPromptScenario;
}
